// Resolve Promotion editor images from saved selections or current event-local
// source records. Allowlisted Firestore paths prevent arbitrary/cross-event reads.
// Saved empty galleries remain empty; source reads never modify records/assets.
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { getEntryImageUrls } from "../../utils/entry_images";

const SOURCE_COLLECTIONS = {
  entries: "cars", cars: "cars", sponsors: "sponsors", vendors: "vendors",
  auction: "auctionItems", store: "store_items", tickets: "tickets",
};
const isDocumentId = (value) => typeof value === "string" && Boolean(value.trim()) && !value.includes("/") && value !== "." && value !== "..";
const hasLegacyImage = (record) => typeof record.imageUrl === "string" && Boolean(record.imageUrl.trim());
const safeImageUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  try {
    const parsed = new URL(value.trim());
    return ["http:", "https:"].includes(parsed.protocol) ? value.trim() : "";
  } catch { return ""; }
};

// Keep Storage-path alignment while removing invalid and duplicate URLs.
const selection = (urls, storagePaths = []) => {
  const seen = new Set();
  const paths = Array.isArray(storagePaths) ? storagePaths : [];
  return urls.flatMap((candidate, index) => {
    const url = safeImageUrl(candidate);
    if (!url || seen.has(url)) return [];
    seen.add(url);
    return [{ url, storagePath: typeof paths[index] === "string" ? paths[index] : "" }];
  });
};

const snapshotImages = (record) => {
  const storedPaths = new Map();
  if (hasLegacyImage(record)) storedPaths.set(record.imageUrl.trim(), record.imageStoragePath || "");
  if (Array.isArray(record.imageUrls)) record.imageUrls.forEach((url, index) => {
    if (typeof url === "string" && !storedPaths.has(url.trim())) storedPaths.set(url.trim(), record.imageStoragePaths?.[index] || "");
  });
  const urls = getEntryImageUrls(record);
  return selection(urls, urls.map((url) => storedPaths.get(url) || ""));
};

const assertRecordScope = (record, eventId) => {
  if (record.eventId != null && record.eventId !== eventId) throw new Error("Promotion image source belongs to another event.");
};

const sourceImages = (data, family) => selection([
  ...getEntryImageUrls(data),
  ...(["sponsors", "vendors"].includes(family) ? [data.logoUrl] : []),
  ...(family === "event" ? [data.eventImage, data.eventLogo] : []),
]);

// A missing known source falls back to the cached snapshot. Transport/permission
// failures propagate so Studio can show its owning error banner instead.
const resolveRecordImages = async (record, eventId) => {
  const fallback = snapshotImages(record);
  // Collection promotions inherit event artwork, never a randomly matched item.
  const family = record.relatedScope === "collection" ? "event" : record.sourceAuctionItemId ? "auction" : record.relatedModule;
  if (!eventId || (!Object.prototype.hasOwnProperty.call(SOURCE_COLLECTIONS, family) && family !== "event")) return fallback;
  if (family === "event") {
    if (record.relatedId && record.relatedId !== eventId) throw new Error("Promotion image source belongs to another event.");
    const [event, settings] = await Promise.all([
      getDoc(doc(db, "events", eventId)),
      getDoc(doc(db, "events", eventId, "settings", "voting")),
    ]);
    if (!event.exists()) return fallback;
    const images = selection([
      ...sourceImages(event.data(), "event").map((image) => image.url),
      ...(settings.exists() ? sourceImages(settings.data(), "event").map((image) => image.url) : []),
    ]);
    return images;
  }

  const sourceId = record.sourceAuctionItemId || record.relatedId;
  let source;
  if (sourceId) {
    if (!isDocumentId(sourceId)) throw new Error("Promotion image source has an invalid record ID.");
    source = await getDoc(doc(db, "events", eventId, SOURCE_COLLECTIONS[family], sourceId));
  } else if (["sponsors", "vendors"].includes(family) && typeof record.relatedName === "string" && record.relatedName.trim()) {
    // Legacy Demo records stored names only. Ambiguous names never select an
    // arbitrary business, and only these two known families support this lookup.
    const matches = await getDocs(query(
      collection(db, "events", eventId, SOURCE_COLLECTIONS[family]),
      where("name", "==", record.relatedName), limit(2),
    ));
    if (matches.docs.length !== 1) return fallback;
    [source] = matches.docs;
  } else return fallback;
  if (!source.exists()) return fallback;
  const data = source.data();
  assertRecordScope(data, eventId);
  if (family === "auction" && data.linkedEntryId) {
    if (!isDocumentId(data.linkedEntryId)) throw new Error("Promotion image source has an invalid linked Entry ID.");
    const entry = await getDoc(doc(db, "events", eventId, "cars", data.linkedEntryId));
    if (entry.exists()) {
      assertRecordScope(entry.data(), eventId);
      const images = sourceImages(entry.data(), "entries");
      if (images.length) return images;
    }
  }
  return sourceImages(data, family);
};

export const getPromotionEditorImages = async ({ record = {}, eventId = null, isSavedPost = false }) => {
  assertRecordScope(record, eventId);
  if (eventId != null && !isDocumentId(eventId)) throw new Error("Promotion image source has an invalid event ID.");
  if (isSavedPost && Array.isArray(record.rawImages)) return selection(record.rawImages.map((image) => image?.url), record.rawImages.map((image) => image?.storagePath));
  if (isSavedPost && Array.isArray(record.imageUrls)) return selection(record.imageUrls, record.imageStoragePaths);
  if (isSavedPost && hasLegacyImage(record)) return selection([record.imageUrl], [record.imageStoragePath]);

  const hasSource = Boolean(record.sourceAuctionItemId || record.relatedScope === "collection" || record.relatedModule === "event" || Object.prototype.hasOwnProperty.call(SOURCE_COLLECTIONS, record.relatedModule));
  if (isSavedPost && !hasSource && record.ideaId) {
    if (!isDocumentId(record.ideaId)) throw new Error("Promotion image source has an invalid Idea ID.");
    const idea = await getDoc(doc(db, "promotion_ideas", record.ideaId));
    if (!idea.exists()) return [];
    const data = idea.data();
    if ((data.eventId || null) !== eventId) throw new Error("Promotion image source belongs to another event.");
    // Resolve exactly once: an Idea's ideaId must never create a recursive chain.
    return resolveRecordImages(data, eventId);
  }
  return resolveRecordImages(record, eventId);
};
